import { doc, getDoc } from 'firebase/firestore';
import { importPKCS8, SignJWT } from 'jose';
import { db } from '../firebase/firebase';

/**
 * Client-side FCM HTTP v1 sender — mirrors the Flutter admin
 * (admin_notification_service.dart). The service-account JSON is read from
 * Firestore `settings/fcm_credentials.privateKeyJson`, an OAuth token is
 * minted in-browser (jose), and the message is POSTed to the FCM v1 API.
 *
 * NOTE: because the SA key is used in the browser, anyone with admin-web
 * access can extract it. This matches the existing mobile approach by request.
 */

const TOKEN_URI = 'https://oauth2.googleapis.com/token';
const FCM_SCOPE = 'https://www.googleapis.com/auth/firebase.messaging';

interface ServiceAccount {
  client_email: string;
  private_key: string;
  project_id: string;
  token_uri?: string;
}

let _sa: ServiceAccount | null = null;
let _accessToken: { value: string; expiresAt: number } | null = null;

/** Reads + caches the service-account JSON from Firestore settings. */
async function loadServiceAccount(): Promise<ServiceAccount> {
  if (_sa) return _sa;
  const snap = await getDoc(doc(db, 'settings', 'fcm_credentials'));
  if (!snap.exists()) {
    throw new Error('FCM Service Account not configured (settings/fcm_credentials).');
  }
  const raw = snap.data()?.privateKeyJson as string | undefined;
  if (!raw) throw new Error('Service Account JSON is empty.');
  const creds = JSON.parse(raw) as ServiceAccount;
  if (!creds.project_id || !creds.private_key || !creds.client_email) {
    throw new Error('Service Account JSON missing project_id / private_key / client_email.');
  }
  _sa = creds;
  return creds;
}

export async function hasCredentials(): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'settings', 'fcm_credentials'));
    const raw = snap.exists() ? (snap.data()?.privateKeyJson as string | undefined) : undefined;
    return !!raw;
  } catch {
    return false;
  }
}

/** Mints (and caches) an OAuth2 access token for the FCM scope. */
async function getAccessToken(): Promise<{ token: string; projectId: string }> {
  const sa = await loadServiceAccount();
  const now = Math.floor(Date.now() / 1000);

  if (_accessToken && _accessToken.expiresAt - 60 > now) {
    return { token: _accessToken.value, projectId: sa.project_id };
  }

  const key = await importPKCS8(sa.private_key, 'RS256');
  const aud = sa.token_uri || TOKEN_URI;
  const assertion = await new SignJWT({ scope: FCM_SCOPE })
    .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
    .setIssuer(sa.client_email)
    .setAudience(aud)
    .setIssuedAt(now)
    .setExpirationTime(now + 3600)
    .sign(key);

  const res = await fetch(aud, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      assertion,
    }),
  });
  if (!res.ok) {
    throw new Error(`OAuth token request failed: ${res.status} ${await res.text()}`);
  }
  const data = await res.json();
  _accessToken = { value: data.access_token, expiresAt: now + (data.expires_in ?? 3600) };
  return { token: _accessToken.value, projectId: sa.project_id };
}

/** Reads a user's FCM token from `users/{userId}.fcmToken`. */
export async function getUserToken(userId: string): Promise<string | null> {
  if (!userId) return null;
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    return snap.exists() ? ((snap.data()?.fcmToken as string) ?? null) : null;
  } catch {
    return null;
  }
}

export interface SendNotificationArgs {
  fcmToken: string;
  title: string;
  body: string;
  imageUrl?: string;
  screen?: string;        // default 'order_details' to match mobile handler
  orderId?: string;
  channelId?: string;
  sound?: string;
}

/**
 * Sends an FCM v1 push. When `imageUrl` is provided it is injected into the
 * notification / data / android / apns blocks exactly like the Flutter sender,
 * and iOS `mutable-content` is set so the image renders.
 */
export async function sendPushNotification(args: SendNotificationArgs): Promise<boolean> {
  const { fcmToken, title, body, imageUrl, screen = 'order_details', orderId = '', channelId, sound } = args;
  if (!fcmToken) throw new Error('User has no FCM token (cannot be notified).');

  const { token, projectId } = await getAccessToken();
  const hasImage = !!(imageUrl && imageUrl.trim());
  const img = imageUrl?.trim();

  const message: any = {
    token: fcmToken,
    notification: {
      title,
      body,
      ...(hasImage ? { image: img } : {}),
    },
    data: {
      click_action: 'FLUTTER_NOTIFICATION_CLICK',
      orderId,
      screen,
      ...(hasImage ? { image: img! } : {}),
    },
    android: {
      notification: {
        channel_id: channelId ?? 'customer_alerts_v1',
        sound: sound ?? 'default',
        ...(hasImage ? { image: img } : {}),
      },
    },
    apns: {
      payload: { aps: { 'mutable-content': 1 } },
      fcm_options: {
        ...(hasImage ? { image: img } : {}),
      },
    },
  };

  const res = await fetch(`https://fcm.googleapis.com/v1/projects/${projectId}/messages:send`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });

  if (!res.ok) {
    throw new Error(`FCM rejected: ${res.status} ${await res.text()}`);
  }
  return true;
}

/** Replaces the {name} token (case-insensitive) with the recipient's name. */
export function personalize(template: string, name?: string | null): string {
  const n = name && name.trim() ? name.trim() : 'there';
  return template.replace(/\{name\}/gi, n);
}

export interface BulkNotifyResult { sent: number; failed: number; skipped: number; }

const CONCURRENCY = 200;

export async function bulkNotify(
  recipients: { fcmToken?: string | null; name?: string | null }[],
  args: Omit<SendNotificationArgs, 'fcmToken'>,
  onProgress?: (done: number, total: number, sent: number, failed: number) => void,
): Promise<BulkNotifyResult> {
  const targets = recipients.filter(r => !!r.fcmToken);
  const skipped = recipients.length - targets.length;

  const messages = targets.map(r => ({
    ...args,
    fcmToken: r.fcmToken!,
    title: personalize(args.title, r.name),
    body: personalize(args.body, r.name),
  }));

  let sent = 0, failed = 0, done = 0;

  for (let i = 0; i < messages.length; i += CONCURRENCY) {
    const chunk = messages.slice(i, i + CONCURRENCY);
    const results = await Promise.allSettled(chunk.map(m => sendPushNotification(m)));
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value) sent++;
      else failed++;
      done++;
    }
    onProgress?.(done, targets.length, sent, failed);
  }

  return { sent, failed, skipped };
}

/** Convenience: look up the user's token, then send. */
export async function notifyUser(
  userId: string,
  args: Omit<SendNotificationArgs, 'fcmToken'> & { fcmToken?: string },
): Promise<boolean> {
  const fcmToken = args.fcmToken || (await getUserToken(userId));
  if (!fcmToken) throw new Error('User has no FCM token (cannot be notified).');
  return sendPushNotification({ ...args, fcmToken });
}
